import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Rarity, Sport } from '@sorare/core/src/__generated__/globalTypes';
import type { Props as ButtonProps } from '@sorare/core/src/atoms/buttons/Button';
import { Button } from '@sorare/core/src/atoms/buttons/Button';
import {
  Dropdown,
  DropdownOptionLabel,
} from '@sorare/core/src/atoms/dropdowns/Dropdown';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { ScarcityIcon } from '@sorare/core/src/atoms/icons/ScarcityIcon';
import { iconCart } from '@sorare/core/src/atoms/icons/system/iconCart.ts';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { Tooltip } from '@sorare/core/src/atoms/tooltip/Tooltip';
import {
  BodyM,
  BodyS,
  HeadlineM,
  LabelXS,
} from '@sorare/core/src/atoms/typography';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { idFromObject } from '@sorare/core/src/gql/idFromObject';
import { useScreenSize } from '@sorare/core/src/hooks/device/useScreenSize';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import { scarcityNames } from '@sorare/core/src/lib/cards';
import { cart as cartMessages } from '@sorare/core/src/lib/glossary';
import { ClickAddMultipleToCart_Origin } from '@sorare/core/src/protos/events/so5/shared/events';

import { useAddMultipleCardsToCart } from '#src/hooks/primaryBuy/useAddMultipleCardsToCart.ts';
import { useCart } from '#src/hooks/useCart.ts';

import { AddWatchlistToCartConfirmDialog } from './AddWatchlistToCartConfirmDialog/index.tsx';
import type {
  AddWatchlistToCartButtonQuery,
  AddWatchlistToCartButtonQueryVariables,
} from './__generated__/index.graphql.ts';

const Header = styled.div`
  padding: var(--unit);
  width: 100%;
`;

const ADD_WATCHLIST_TO_CART_BUTTON_QUERY = gql`
  query AddWatchlistToCartButtonQuery(
    $watchlistId: ID!
    $withCollectionPredictions: Boolean = true
  ) {
    market {
      id
      watchlist(id: $watchlistId) {
        slug
        id
        sport
        limitedSellableCards: sellableCards(rarity: limited) {
          slug
          ...useCart_anyCard
          ...useAddMultipleCardsToCart_anyCard
          ...AddWatchlistToCartConfirmDialog_anyCard
        }
        rareSellableCards: sellableCards(rarity: rare) {
          slug
          ...useCart_anyCard
          ...useAddMultipleCardsToCart_anyCard
          ...AddWatchlistToCartConfirmDialog_anyCard
        }
        superRareSellableCards: sellableCards(rarity: super_rare) {
          slug
          ...useCart_anyCard
          ...useAddMultipleCardsToCart_anyCard
          ...AddWatchlistToCartConfirmDialog_anyCard
        }
      }
    }
  }
  ${AddWatchlistToCartConfirmDialog.fragments.anyCard}
  ${useAddMultipleCardsToCart.fragments.anyCard}
  ${useCart.fragments.anyCard}
` as TypedDocumentNode<
  AddWatchlistToCartButtonQuery,
  AddWatchlistToCartButtonQueryVariables
>;

type Props = {
  watchlistId: string;
} & ButtonProps;

const AVAILABLE_RARITIES = [
  Rarity.limited,
  Rarity.rare,
  Rarity.super_rare,
] as const;

type AvailableRarity = (typeof AVAILABLE_RARITIES)[number];

export const LazyAddWatchlistToCartButton = ({
  watchlistId,
  ...props
}: Props) => {
  const { currentUser } = useCurrentUserContext();
  const { data, loading } = useQuery(ADD_WATCHLIST_TO_CART_BUTTON_QUERY, {
    variables: { watchlistId: idFromObject(watchlistId) },
    skip: !watchlistId || !currentUser,
  });

  const watchlist = data?.market?.watchlist;

  const { up: tabletOrAbove } = useScreenSize('tablet');
  const [open, setOpen] = useState(false);
  const [selectedRarity, setSelectedRarity] = useState<AvailableRarity | null>(
    null
  );
  const { similarCardisInCart } = useCart();

  const {
    sport = Sport.FOOTBALL,
    limitedSellableCards,
    rareSellableCards,
    superRareSellableCards,
  } = watchlist || {};
  const mappedSellableCards = {
    [Rarity.limited]: {
      sellable: limitedSellableCards,
      inCart: limitedSellableCards?.filter(c => similarCardisInCart(c)),
      available: limitedSellableCards?.filter(c => !similarCardisInCart(c)),
    },
    [Rarity.rare]: {
      sellable: rareSellableCards,
      inCart: rareSellableCards?.filter(c => similarCardisInCart(c)),
      available: rareSellableCards?.filter(c => !similarCardisInCart(c)),
    },
    [Rarity.super_rare]: {
      sellable: superRareSellableCards,
      inCart: superRareSellableCards?.filter(c => similarCardisInCart(c)),
      available: superRareSellableCards?.filter(c => !similarCardisInCart(c)),
    },
  };

  const availableRarities = AVAILABLE_RARITIES.filter(
    r => (mappedSellableCards[r].sellable?.length || 0) > 0
  );

  const availableCards = (
    (selectedRarity && mappedSellableCards[selectedRarity].available) ||
    []
  ).filter(Boolean);

  const { addMultipleCardsToCart } = useAddMultipleCardsToCart({
    rarity: selectedRarity || Rarity.limited,
    cards: availableCards,
    sport,
    origin: ClickAddMultipleToCart_Origin.WATCHLIST,
    watchlistId: watchlist?.id,
  });

  const addMissingCardsToCart = async () => {
    if (!selectedRarity) return;
    await addMultipleCardsToCart.mutate();
  };

  const disabled = !availableRarities.length;

  if (loading || !currentUser) return null;

  return (
    <>
      <Tooltip
        title={
          <BodyS className="text-center">
            <FormattedMessage defaultMessage="No cards available" />
          </BodyS>
        }
        skip={!disabled}
      >
        <Dropdown
          gap={8}
          open={open}
          onClose={() => {
            setOpen(false);
          }}
          fullWidth
          label={p => (
            <Button
              {...props}
              {...p}
              onClick={() => setOpen(true)}
              disabled={disabled}
              startIcon={<FontAwesomeIcon icon={iconCart} />}
            >
              <FormattedMessage {...cartMessages.addAllToCart} />
            </Button>
          )}
        >
          <Vertical gap={0}>
            {!tabletOrAbove && (
              <Header>
                <HeadlineM className="text-center">
                  <FormattedMessage {...cartMessages.addAllToCart} />
                </HeadlineM>
              </Header>
            )}
            {availableRarities.map(r => (
              <DropdownOptionLabel
                key={r}
                onClick={() => {
                  if (!mappedSellableCards[r].available?.length) return;
                  setSelectedRarity(r);
                  setOpen(false);
                }}
                className={
                  !mappedSellableCards[r].available?.length ? 'disabled' : ''
                }
              >
                <ScarcityIcon rarity={r} size={3} />
                <Vertical as="span" gap={0}>
                  <BodyM color="var(--c-white)">{scarcityNames[r]}</BodyM>
                  {!!mappedSellableCards[r].inCart?.length && (
                    <LabelXS>
                      <FormattedMessage
                        defaultMessage="{count, plural, one {1 card} other {# cards}} already in cart"
                        values={{
                          count: mappedSellableCards[r].inCart.length,
                        }}
                      />
                    </LabelXS>
                  )}
                </Vertical>
              </DropdownOptionLabel>
            ))}
          </Vertical>
        </Dropdown>
      </Tooltip>
      <AddWatchlistToCartConfirmDialog
        cards={availableCards}
        open={!!selectedRarity}
        onConfirm={addMissingCardsToCart}
        onClose={() => setSelectedRarity(null)}
      />
    </>
  );
};
